/**
 * Managed column visibility utilities for MUI Data Grid (Premium).
 *
 * This hook centralizes the rules for which columns must always be hidden
 * ("forced") and which should start hidden by default ("defaults") while
 * still allowing users to manage visibility thereafter. It also ensures that
 * the Columns panel in the Data Grid does not present controls for columns
 * that are not hideable.
 *
 * Key behaviors:
 * - Forced hidden fields are always hidden and cannot be toggled by users.
 * - Default hidden fields start hidden, but users can later show/hide them.
 * - The Columns panel hides disabled entries and the auto-selection checkbox.
 * - When configuration changes, the model is reconciled predictably without
 *   clobbering user choices where appropriate.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

/** Unique ID for the dynamically injected <style> element. */
const STYLE_ID = 'wa-hide-disabled-grid-columns';
/** Reference counter so multiple hook consumers can share the same style. */
let styleRefCount = 0;

/**
 * Ensures the Data Grid Columns panel hides disabled rows and the auto
 * selection checkbox entry. Injects a single <style> tag into <head> and
 * returns a cleanup function that decrements a reference counter and removes
 * the style once no consumers remain.
 */
const ensureColumnMenuStyle = () => {
  if (typeof document === 'undefined') {
    return () => {};
  }

  let styleEl = document.getElementById(STYLE_ID);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = STYLE_ID;
    styleEl.textContent = `
      .MuiDataGrid-columnsManagement .MuiFormControlLabel-root.Mui-disabled {
        display: none !important;
      }
      .MuiDataGrid-columnsManagement label.MuiFormControlLabel-root.Mui-disabled.MuiDataGrid-columnsManagementRow {
        display: none !important;
      }
      /* Hide the auto-generated Checkbox selection column from the Columns panel */
      .MuiDataGrid-columnsManagement [data-field="__check__"] {
        display: none !important;
      }
      /* Fallbacks for different DOM shapes */
      .MuiDataGrid-columnsManagement label.MuiDataGrid-columnsManagementRow:has(input[name="__check__"]) {
        display: none !important;
      }
      .MuiDataGrid-columnsManagement input[name="__check__"] {
        display: none !important;
      }
    `;
    document.head.appendChild(styleEl);
  }

  styleRefCount += 1;

  return () => {
    styleRefCount -= 1;
    if (styleRefCount <= 0) {
      const existing = document.getElementById(STYLE_ID);
      existing?.remove();
      styleRefCount = 0;
    }
  };
};

/**
 * Normalizes an array of column field names by trimming, filtering empty
 * values, and de-duplicating while preserving set semantics.
 */
const normalizeFields = (fields?: string[]) => {
  if (!fields?.length) return [];
  const set = new Set<string>();
  fields.forEach((field) => {
    if (!field) return;
    const trimmed = field.trim();
    if (trimmed) {
      set.add(trimmed);
    }
  });
  return Array.from(set);
};

/**
 * Reads a column's visibility flag, distinguishing "explicitly hidden"
 * (`false`) from "absent / visible by default" (`undefined`). MUI types
 * `GridColumnVisibilityModel` values as `boolean`, but at runtime a field is
 * simply absent until it has been hidden — the reconciliation below relies on
 * that distinction, so the `=== false` checks are intentional, not redundant.
 */
const readVisibility = (
  model: GridColumnVisibilityModel,
  field: string,
): boolean | undefined => (field in model ? model[field] : undefined);

/** Options that control how column visibility is managed. */
interface UseManagedColumnVisibilityOptions {
  forcedHiddenFields?: string[];
  defaultHiddenFields?: string[];
  hideDisabledColumnMenu?: boolean;
}

/** Values returned by the managed column visibility hook. */
interface UseManagedColumnVisibilityReturn<T extends GridValidRowModel> {
  columnVisibilityModel: GridColumnVisibilityModel;
  handleColumnVisibilityChange: (model: GridColumnVisibilityModel) => void;
  applyColumnVisibility: (columns: GridColDef<T>[]) => GridColDef<T>[];
  /**
   * The default visibility baseline (forced + default-hidden fields). Pass this
   * to the Columns panel's Reset so it can deterministically restore the default
   * layout, instead of relying on MUI's internal initial-model tracking which
   * drifts to the current selection whenever the `columns` prop reference
   * changes (e.g. on data load or grouping changes) — the EUD-612 root cause.
   */
  defaultColumnVisibilityModel: GridColumnVisibilityModel;
}

/**
 * useManagedColumnVisibility
 *
 * Provides a controlled `columnVisibilityModel` along with helpers that
 * enforce the rules defined by `forcedHiddenFields` and `defaultHiddenFields`.
 *
 * Usage:
 * - Pass `columnVisibilityModel` and `onColumnVisibilityModelChange` (wired to
 *   `handleColumnVisibilityChange`) into the Data Grid.
 * - Wrap your column definitions with `applyColumnVisibility` so forced-hidden
 *   columns are marked `hideable: false` and do not present toggles.
 */
export const useManagedColumnVisibility = <T extends GridValidRowModel>(
  options: UseManagedColumnVisibilityOptions,
): UseManagedColumnVisibilityReturn<T> => {
  const {
    forcedHiddenFields = [],
    defaultHiddenFields = [],
    hideDisabledColumnMenu = true,
  } = options;

  // Optionally inject the style that hides disabled rows/checkbox in the
  // Columns panel when this hook is active.
  useEffect(() => {
    if (!hideDisabledColumnMenu) return;
    return ensureColumnMenuStyle();
  }, [hideDisabledColumnMenu]);

  // Normalize and memoize the incoming configuration arrays to stable sets.
  const normalizedForced = useMemo(
    () => normalizeFields(forcedHiddenFields),
    [forcedHiddenFields],
  );
  const normalizedDefault = useMemo(
    () => normalizeFields(defaultHiddenFields),
    [defaultHiddenFields],
  );

  const forcedSet = useMemo(
    () => new Set(normalizedForced),
    [normalizedForced],
  );
  const defaultSet = useMemo(
    () => new Set(normalizedDefault),
    [normalizedDefault],
  );

  // The visibility model the grid starts from: forced fields and default-hidden
  // fields are hidden. Exposed for a deterministic Columns-panel Reset.
  const defaultColumnVisibilityModel =
    useMemo<GridColumnVisibilityModel>(() => {
      const model: GridColumnVisibilityModel = {};
      forcedSet.forEach((field) => {
        model[field] = false;
      });
      defaultSet.forEach((field) => {
        if (!(field in model)) {
          model[field] = false;
        }
      });
      return model;
    }, [forcedSet, defaultSet]);

  const lastForcedRef = useRef<Set<string>>(new Set());
  const appliedDefaultRef = useRef<Set<string>>(new Set());

  // Initialize the visibility model with forced and default hidden fields.
  // Forced fields are always hidden. Default fields start hidden, but users can
  // subsequently change them; we track which defaults have been applied so we
  // do not re-apply them after user interaction.
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(() => {
      lastForcedRef.current = new Set(forcedSet);
      appliedDefaultRef.current = new Set(defaultSet);
      return { ...defaultColumnVisibilityModel };
    });

  // Reconcile changes when the sets of forced/default fields change.
  // - Remove defaults that are no longer configured
  // - Enforce forced-hidden fields
  // - Apply default hidden fields only once (do not override user choices)
  // - Release previously forced fields when no longer forced/default
  useEffect(() => {
    setColumnVisibilityModel((prev) => {
      const next = { ...prev };
      let changed = false;

      // Remove defaults that are no longer configured
      appliedDefaultRef.current.forEach((field) => {
        if (!defaultSet.has(field)) {
          appliedDefaultRef.current.delete(field);
          if (!forcedSet.has(field) && readVisibility(next, field) === false) {
            Reflect.deleteProperty(next, field);
            changed = true;
          }
        }
      });

      // Enforce forced-hidden fields
      forcedSet.forEach((field) => {
        if (readVisibility(next, field) !== false) {
          next[field] = false;
          changed = true;
        }
      });

      // Apply default hidden fields only if not already managed by the user
      defaultSet.forEach((field) => {
        if (!appliedDefaultRef.current.has(field)) {
          if (!(field in next)) {
            next[field] = false;
            changed = true;
          }
          appliedDefaultRef.current.add(field);
        }
      });

      // Release previously forced fields that are no longer forced or default
      lastForcedRef.current.forEach((field) => {
        if (
          !forcedSet.has(field) &&
          !defaultSet.has(field) &&
          readVisibility(next, field) === false
        ) {
          Reflect.deleteProperty(next, field);
          changed = true;
        }
      });

      lastForcedRef.current = new Set(forcedSet);

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `changed` is mutated inside the forEach callbacks above; the rule's flow analysis can't see closure mutations and wrongly treats it as always-false. Returning `prev` unchanged avoids a needless state update.
      return changed ? next : prev;
    });
  }, [forcedSet, defaultSet]);

  // Wrap the grid's visibility model change handler so forced fields remain
  // hidden regardless of user toggles.
  const handleColumnVisibilityChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const next = { ...model };
      forcedSet.forEach((field) => {
        next[field] = false;
      });
      setColumnVisibilityModel(next);
    },
    [forcedSet],
  );

  // Ensure column definitions reflect non-hideable state for forced fields so
  // the UI does not present toggles for them in menus/toolbars.
  const applyColumnVisibility = useCallback(
    (columns: GridColDef<T>[]) => {
      if (!forcedSet.size) {
        return columns;
      }

      let mutated = false;

      const updated = columns.map((column) => {
        const field = column.field;
        if (forcedSet.has(field) && column.hideable !== false) {
          mutated = true;
          return { ...column, hideable: false };
        }
        return column;
      });

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- `mutated` is set inside the map callback above; the rule's flow analysis can't see it and wrongly treats it as always-false. This guard is load-bearing: returning the original `columns` reference when nothing changed keeps the prop referentially stable (a churning columns ref is the EUD-612 root cause).
      return mutated ? updated : columns;
    },
    [forcedSet],
  );

  return {
    columnVisibilityModel,
    handleColumnVisibilityChange,
    applyColumnVisibility,
    defaultColumnVisibilityModel,
  };
};
